import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, AlertTriangle, Camera, Paperclip, CheckCircle2 } from 'lucide-react';
export function ReportScreen() {
  const navigate = useNavigate();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const reasons = ['No Show', 'Unprofessional Behavior', 'Fraud', 'Wrong Service', 'Harassment', 'Fake Credentials', 'Poor Care Experience'];
  if (submitted) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6 text-center" data-id="element-1531">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6" data-id="element-1532">
          <CheckCircle2 className="w-10 h-10 text-blue-600" data-id="element-1533" />
        </motion.div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1534">
          Report Submitted
        </h2>
        <p className="text-slate-600 mb-4 max-w-[280px]" data-id="element-1535">
          Our team will review this case within 24-48 hours.
        </p>
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl mb-8 max-w-[300px]" data-id="element-1536">
          <p className="text-sm text-amber-800" data-id="element-1537">
            Payment is temporarily on hold while this case is reviewed.
          </p>
        </div>
        <button onClick={() => navigate('/home')} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-blue-700 transition-colors" data-id="element-1538">
          Back to Home
        </button>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="flex flex-col min-h-screen bg-slate-50" data-id="element-1539">
      <div className="bg-white px-6 pt-12 pb-4 shadow-sm sticky top-0 z-10 flex items-center gap-3" data-id="element-1540">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-slate-50 rounded-full" data-id="element-1541">
          <ArrowLeft className="w-6 h-6 text-slate-700" data-id="element-1542" />
        </button>
        <h1 className="text-xl font-bold text-slate-900" data-id="element-1543">Report Issue</h1>
      </div>

      <div className="p-6 flex-1 overflow-y-auto" data-id="element-1544">
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3 mb-6" data-id="element-1545">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" data-id="element-1546" />
          <p className="text-sm text-amber-800" data-id="element-1547">
            We take reports seriously. False reports may lead to account
            suspension.
          </p>
        </div>

        <div className="space-y-6" data-id="element-1548">
          <div data-id="element-1549">
            <h3 className="font-semibold text-slate-900 mb-3" data-id="element-1550">
              Reason for report
            </h3>
            <div className="space-y-2" data-id="element-1551">
              {reasons.map(reason => <label key={reason} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${selectedReason === reason ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-1552">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${selectedReason === reason ? 'border-blue-600' : 'border-slate-300'}`} data-id="element-1553">
                    {selectedReason === reason && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" data-id="element-1554" />}
                  </div>
                  <span className={`text-sm font-medium ${selectedReason === reason ? 'text-blue-900' : 'text-slate-700'}`} data-id="element-1555">
                    {reason}
                  </span>
                </label>)}
            </div>
          </div>

          <div data-id="element-1556">
            <h3 className="font-semibold text-slate-900 mb-3" data-id="element-1557">Description</h3>
            <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl p-4 text-sm focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 min-h-[120px]" placeholder="Please provide details about what happened..." data-id="element-1558" />
          </div>

          <div data-id="element-1559">
            <h3 className="font-semibold text-slate-900 mb-3" data-id="element-1560">
              Evidence (Optional)
            </h3>
            <div className="flex gap-3" data-id="element-1561">
              <button className="flex-1 py-4 bg-white border border-slate-200 border-dashed rounded-xl flex flex-col items-center gap-2 text-slate-500 hover:bg-slate-50 transition-colors" data-id="element-1562">
                <Camera className="w-6 h-6" data-id="element-1563" />
                <span className="text-xs font-medium" data-id="element-1564">Take Photo</span>
              </button>
              <button className="flex-1 py-4 bg-white border border-slate-200 border-dashed rounded-xl flex flex-col items-center gap-2 text-slate-500 hover:bg-slate-50 transition-colors" data-id="element-1565">
                <Paperclip className="w-6 h-6" data-id="element-1566" />
                <span className="text-xs font-medium" data-id="element-1567">Upload File</span>
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2" data-id="element-1568">
              Screenshots of chats, receipts, or other proof.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 bg-white border-t border-slate-100" data-id="element-1569">
        <button onClick={() => setSubmitted(true)} disabled={!selectedReason || description.length < 10} className="w-full bg-red-600 text-white py-3.5 rounded-xl font-semibold text-lg shadow-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1570">
          Submit Report
        </button>
      </div>
    </motion.div>;
}